import React, { useState } from 'react';
import { Typography, Row, Col, Card, Button, Switch, Table, Divider, Space, Badge, Tabs } from 'antd';
import { 
  CheckOutlined, 
  CloseOutlined, 
  WhatsAppOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ShopOutlined,
  BarChartOutlined,
  ApiOutlined,
  SafetyOutlined,
  CrownOutlined,
  StarOutlined,
  MenuOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import logo from '../assets/logo.png';
import './Pricing.css';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const Pricing = () => {
  const [annually, setAnnually] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Définition des plans avec nouvelles couleurs
  const plans = [
    {
      name: 'Découverte',
      price: '0',
      period: '/mois',
      save: null,
      description: 'Idéal pour les entrepreneurs et micro-entreprises souhaitant tester l\'automatisation WhatsApp avec Shopify',
      features: [
        '1 numéro WhatsApp',
        '15 messages automatisés (puis déconnexion)',
        '1 boutique',
        '5 produits maximum',
        '2 modèles de messages automatiques',
        'Connexion Shopify (sans import)',
        '1 livreur (gestion manuelle)',
        '1 zone de livraison simple',
        '1 utilisateur',
        'Documentation en ligne'
      ],
      limitations: [
        'Pas d\'import automatique',
        'Pas de synchronisation stock',
        'Support documentation uniquement',
        'Déconnexion automatique après 15 messages ou 2 semaines',
        'Non renouvelable chaque mois'
      ],
      buttonText: 'Démarrer Gratuitement',
      popular: false,
      color: '#6B7280',
      bgColor: '#F9FAFB',
      readyPercent: 95
    },
    {
      name: 'Essentiel',
      price: annually ? '99 000' : '9 900',
      period: annually ? '/an' : '/mois',
      save: annually ? 'Économisez 16%' : null,
      description: 'Pour les PME qui veulent structurer leurs ventes Shopify et automatiser les notifications clés',
      features: [
        '1 numéro WhatsApp',
        '300 messages automatisés/mois',
        '1 boutique',
        '200 produits',
        '10 modèles de messages automatiques',
        'Import automatique commandes Shopify',
        'Import clients Shopify',
        'Synchronisation basique des stocks',
        'Suivi basique des commandes',
        '3 livreurs avec app basique',
        '3 zones de livraison',
        '1 utilisateur',
        'Rapports de ventes standards (30 jours)',
        'Support email (48h)',
        'Authentification sécurisée'
      ],
      limitations: [
        'Pas d\'IA avancée',
        'Analytics limitées'
      ],
      buttonText: 'Choisir Essentiel',
      popular: true,
      color: '#2563eb',
      bgColor: '#EFF6FF',
      readyPercent: 90
    },
    {
      name: 'Croissance',
      price: annually ? '299 000' : '29 900',
      period: annually ? '/an' : '/mois',
      save: annually ? 'Économisez 16%' : null,
      description: 'Pour les entreprises en expansion avec automatisation puissante et gestion multi-boutiques',
      features: [
        '1 numéro WhatsApp',
        '1 500 messages automatisés/mois',
        '2 boutiques/points de vente',
        '1 000 produits',
        'Automatisation avancée (50 modèles)',
        'Campagnes marketing automatisées',
        'Gestion des caissiers',
        'Synchronisation automatique des stocks',
        '10 livreurs avec suivi GPS',
        '10 zones de livraison avec calcul auto',
        '3 utilisateurs',
        'Rapports de performance livreurs',
        'Intégration Shopify complète',
        'Intégration POS',
        'Analytics avancées (1 an) + exports',
        'Comptabilité basique + commissions',
        'Support prioritaire (24h) + chat',
        'Système de points fidélité avancé'
      ],
      limitations: [
        'Pas d\'intégration Gmail',
        'Mobile Money en cours',
        'IA prédictive non disponible'
      ],
      buttonText: 'Choisir Croissance',
      popular: false,
      color: '#0891b2',
      bgColor: '#ECFEFF',
      readyPercent: 70
    },
    {
      name: 'Excellence',
      price: 'Sur Devis',
      period: '',
      save: null,
      description: 'Solution sur mesure pour grandes entreprises avec besoins spécifiques et évolutivité illimitée',
      features: [
        '2 numéros WhatsApp',
        'Messages automatisés illimités',
        'Boutiques/points de vente illimités',
        'Produits illimités',
        'Automatisation personnalisée',
        'Marketing automation complet',
        'Gestion centralisée multi-entités',
        'Livreurs illimités avec app personnalisée',
        'Zones de livraison illimitées',
        'Utilisateurs illimités',
        'API complète pour développeurs',
        'Intégrations sur mesure',
        'Analytics entièrement personnalisables',
        'Support dédié 24/7 avec responsable compte',
        'Formation personnalisée sur site',
        'SLA garanti 99.9%'
      ],
      limitations: [
        'Marque blanche en développement',
        'Conformité RGPD en cours',
        'Apps mobiles en roadmap'
      ],
      buttonText: 'Contacter les Ventes',
      popular: false,
      color: '#7C3AED',
      bgColor: '#EDE9FE',
      readyPercent: 50
    },
  ];

  // Données pour le tableau comparatif basées sur les fonctionnalités réelles
  const comparisonData = [
    // Communications WhatsApp (100% développé)
    { category: 'Communications WhatsApp', feature: 'Numéros WhatsApp', decouverte: '1', essentiel: '1', croissance: '1', excellence: '2' },
    { category: 'Communications WhatsApp', feature: 'Messages automatisés/mois', decouverte: '15', essentiel: '300', croissance: '1 500', excellence: 'Illimités' },
    { category: 'Communications WhatsApp', feature: 'Modèles de messages', decouverte: '2 basiques', essentiel: '10 basiques', croissance: '50 avancés', excellence: 'Personnalisés' },
    { category: 'Communications WhatsApp', feature: 'Connexion QR Code', decouverte: true, essentiel: true, croissance: true, excellence: true },
    
    // Gestion boutiques (100% développé)
    { category: 'Gestion boutiques', feature: 'Nombre de boutiques', decouverte: '1', essentiel: '1', croissance: '2', excellence: 'Illimitées' },
    { category: 'Gestion boutiques', feature: 'Produits au catalogue', decouverte: '5', essentiel: '200', croissance: '1 000', excellence: 'Illimités' },
    { category: 'Gestion boutiques', feature: 'Utilisateurs/Employés', decouverte: '1', essentiel: '1', croissance: '3', excellence: 'Illimités' },
    { category: 'Gestion boutiques', feature: 'Gestion des caissiers', decouverte: false, essentiel: false, croissance: true, excellence: true },
    
    // Intégration Shopify (95% développé)
    { category: 'Intégration Shopify', feature: 'Connexion Shopify', decouverte: 'Basique', essentiel: true, croissance: true, excellence: true },
    { category: 'Intégration Shopify', feature: 'Import automatique commandes', decouverte: false, essentiel: true, croissance: true, excellence: true },
    { category: 'Intégration Shopify', feature: 'Import clients', decouverte: false, essentiel: true, croissance: true, excellence: true },
    { category: 'Intégration Shopify', feature: 'Synchronisation stocks', decouverte: false, essentiel: 'Basique', croissance: 'Automatique', excellence: 'Temps réel' },
    { category: 'Intégration Shopify', feature: 'Calcul points fidélité', decouverte: false, essentiel: true, croissance: true, excellence: true },
    
    // Point de Vente POS (100% développé)
    { category: 'Point de Vente', feature: 'Interface POS', decouverte: false, essentiel: false, croissance: true, excellence: true },
    { category: 'Point de Vente', feature: 'Lecture code-barres', decouverte: false, essentiel: false, croissance: true, excellence: true },
    { category: 'Point de Vente', feature: 'Gestion variantes produits', decouverte: false, essentiel: false, croissance: true, excellence: true },
    { category: 'Point de Vente', feature: 'Méthodes de paiement', decouverte: false, essentiel: false, croissance: 'Espèces/Carte', excellence: 'Toutes' },
    { category: 'Point de Vente', feature: 'Génération reçus WhatsApp', decouverte: false, essentiel: false, croissance: true, excellence: true },
    
    // Livraisons (90% développé)
    { category: 'Livraisons', feature: 'Nombre de livreurs', decouverte: '1', essentiel: '3', croissance: '10', excellence: 'Illimités' },
    { category: 'Livraisons', feature: 'Zones de livraison', decouverte: '1 simple', essentiel: '3 zones', croissance: '10 + calcul auto', excellence: 'Illimitées + optimisation' },
    { category: 'Livraisons', feature: 'Gestion disponibilités', decouverte: false, essentiel: true, croissance: true, excellence: true },
    { category: 'Livraisons', feature: 'Statistiques livreurs', decouverte: false, essentiel: 'Basiques', croissance: 'Avancées', excellence: 'Personnalisées' },
    { category: 'Livraisons', feature: 'Attribution automatique', decouverte: false, essentiel: false, croissance: true, excellence: true },
    
    // Analytics et Reporting (60% développé)
    { category: 'Analytics', feature: 'Historique des données', decouverte: '7 jours', essentiel: '30 jours', croissance: '1 an', excellence: 'Illimité' },
    { category: 'Analytics', feature: 'Rapports de ventes', decouverte: false, essentiel: 'Standards', croissance: 'Avancés + exports', excellence: 'Personnalisables' },
    { category: 'Analytics', feature: 'Performance livreurs', decouverte: false, essentiel: false, croissance: true, excellence: true },
    { category: 'Analytics', feature: 'Tableaux de bord', decouverte: false, essentiel: 'Basique', croissance: 'Avancé', excellence: 'Personnalisé' },
    
    // Support
    { category: 'Support', feature: 'Type de support', decouverte: 'Documentation', essentiel: 'Email (48h)', croissance: 'Prioritaire (24h)', excellence: 'Dédié 24/7' },
    { category: 'Support', feature: 'Formation', decouverte: false, essentiel: false, croissance: 'Documentation', excellence: 'Personnalisée' },
    { category: 'Support', feature: 'SLA garanti', decouverte: false, essentiel: false, croissance: false, excellence: '99.9%' },
    
    // Fonctionnalités avancées (partiellement développées)
    { category: 'Avancé', feature: 'API pour développeurs', decouverte: false, essentiel: false, croissance: false, excellence: 'Complète' },
    { category: 'Avancé', feature: 'Intégrations sur mesure', decouverte: false, essentiel: false, croissance: false, excellence: true },
    { category: 'Avancé', feature: 'Marketing automation', decouverte: false, essentiel: false, croissance: 'Basique', excellence: 'Complet' }
  ];

  // Colonnes pour le tableau comparatif
  const columns = [
    {
      title: 'Fonctionnalité',
      dataIndex: 'feature',
      key: 'feature',
      width: '35%',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
        </div>
      )
    },
    {
      title: 'Découverte',
      key: 'decouverte',
      align: 'center',
      width: '16%',
      render: (_, record) => {
        if (record.decouverte === false) return <CloseOutlined style={{ color: '#EF4444' }} />;
        if (record.decouverte === true) return <CheckOutlined style={{ color: '#22C55E' }} />;
        return <Text>{record.decouverte}</Text>;
      }
    },
    {
      title: 'Essentiel',
      key: 'essentiel',
      align: 'center',
      width: '16%',
      render: (_, record) => {
        if (record.essentiel === false) return <CloseOutlined style={{ color: '#EF4444' }} />;
        if (record.essentiel === true) return <CheckOutlined style={{ color: '#22C55E' }} />;
        return <Text strong style={{ color: '#22C55E' }}>{record.essentiel}</Text>;
      }
    },
    {
      title: 'Croissance',
      key: 'croissance',
      align: 'center',
      width: '16%',
      render: (_, record) => {
        if (record.croissance === false) return <CloseOutlined style={{ color: '#EF4444' }} />;
        if (record.croissance === true) return <CheckOutlined style={{ color: '#22C55E' }} />;
        return <Text strong style={{ color: '#16A34A' }}>{record.croissance}</Text>;
      }
    },
    {
      title: 'Excellence',
      key: 'excellence',
      align: 'center',
      width: '16%',
      render: (_, record) => {
        if (record.excellence === false) return <CloseOutlined style={{ color: '#EF4444' }} />;
        if (record.excellence === true) return <CheckOutlined style={{ color: '#22C55E' }} />;
        return <Text strong style={{ color: '#7C3AED' }}>{record.excellence}</Text>;
      }
    }
  ];

  // Grouper les données par catégorie pour l'affichage
  const groupedData = comparisonData.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="pricing-root">
      {/* Header avec nouvelles couleurs */}
      <header className="landing-header">
        <div className="landing-header-inner">
          {/* Logo */}
          <div className="landing-logo">
            <div className="landing-logo-icon">
              <img src={logo} alt="Mercuri Shop" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
            </div>
            <Text className="landing-logo-text">Mercuri Shop</Text>
          </div>

          {/* Navigation Desktop */}
          <nav className="landing-nav landing-nav-desktop">
            <Link to="/#integrations" className="landing-nav-link">Intégrations</Link>
            <Link to="/#temoignages" className="landing-nav-link">Témoignages</Link>
            <Text className="landing-nav-link" style={{ color: '#2563eb', fontWeight: 700 }}>Tarifs</Text>
          </nav>

          {/* CTA Buttons Desktop avec nouvelles couleurs */}
          <div className="landing-header-actions landing-header-actions-desktop">
            <Link to="/contact">
              <Button 
                type="primary" 
                className="pricing-header-cta-primary"
              >
                Demander une démo
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                className="pricing-header-cta-secondary"
              >
                Se connecter
              </Button>
            </Link>
          </div>

          {/* Menu Hamburger Mobile */}
          <div className="landing-header-mobile-toggle">
            <Button 
              icon={mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              onClick={toggleMobileMenu}
              type="text"
              className="mobile-menu-toggle"
            />
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {mobileMenuOpen && (
          <div className="landing-header-mobile-menu">
            <nav className="landing-nav-mobile">
              <Link to="/#integrations" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Intégrations
              </Link>
              <Link to="/#temoignages" className="landing-nav-link-mobile" onClick={toggleMobileMenu}>
                Témoignages
              </Link>
              <Text className="landing-nav-link-mobile" style={{ color: '#2563eb', fontWeight: 700 }}>
                Tarifs
              </Text>
            </nav>
            
            <div className="landing-header-actions-mobile">
              <Link to="/contact">
                <Button 
                  type="primary" 
                  block
                  className="pricing-header-cta-primary-mobile"
                  onClick={toggleMobileMenu}
                >
                  Demander une démo
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  block
                  className="pricing-header-cta-secondary-mobile"
                  onClick={toggleMobileMenu}
                >
                  Se connecter
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section avec design moderne */}
      <section className="pricing-hero">
        <div className="pricing-hero-container">
          <Title level={1} className="pricing-hero-title">
            Automatisez votre business avec WhatsApp
          </Title>
          <Paragraph className="pricing-hero-description">
            Des solutions éprouvées et opérationnelles pour toutes les tailles d'entreprises. Commencez gratuitement et évoluez selon vos besoins.
          </Paragraph>
          
          {/* Toggle facturation avec nouvelles couleurs */}
          <div className="pricing-toggle-container">
            <Text className="pricing-toggle-text">Mensuel</Text>
            <Switch 
              checked={annually} 
              onChange={setAnnually}
              className="pricing-toggle-switch"
            />
            <Text className="pricing-toggle-text">
              Annuel <Badge count="16% OFF" className="pricing-toggle-badge" />
            </Text>
          </div>
        </div>
      </section>

      {/* Plans Cards avec couleurs modernisées */}
      <section className="pricing-plans-section">
        <div className="pricing-plans-container">
          <Row gutter={[24, 32]} justify="center">
            {plans.map((plan, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card className={`pricing-plan-card ${plan.popular ? 'popular' : ''}`}>
                  {/* Badge populaire */}
                  {plan.popular && (
                    <div className="pricing-plan-badge">
                      <StarOutlined /> PLUS POPULAIRE
                    </div>
                  )}

                  {/* Badge de statut de développement */}
                  <div className={`pricing-plan-status ${
                    plan.readyPercent >= 90 ? 'ready' : 
                    plan.readyPercent >= 70 ? 'soon' : 'progress'
                  }`}>
                    {plan.readyPercent >= 90 ? 'PRÊT' : plan.readyPercent >= 70 ? 'BIENTÔT' : 'EN COURS'}
                  </div>

                  {/* Icône du plan */}
                  <div className={`pricing-plan-icon ${plan.popular ? 'popular-offset' : ''}`} style={{ backgroundColor: plan.bgColor }}>
                    {plan.name === 'Découverte' && <WhatsAppOutlined style={{ fontSize: '28px', color: plan.color }} />}
                    {plan.name === 'Essentiel' && <ShoppingCartOutlined style={{ fontSize: '28px', color: plan.color }} />}
                    {plan.name === 'Croissance' && <TeamOutlined style={{ fontSize: '28px', color: plan.color }} />}
                    {plan.name === 'Excellence' && <CrownOutlined style={{ fontSize: '28px', color: plan.color }} />}
                  </div>
                  
                  {/* Nom du plan */}
                  <Title level={3} className="pricing-plan-title" style={{ color: plan.color }}>
                    {plan.name}
                  </Title>
                  
                  {/* Prix */}
                  <div style={{ marginBottom: '24px' }}>
                    <Text className={`pricing-plan-price ${plan.price === 'Sur Devis' ? 'devis' : ''}`}>
                      {plan.price === 'Sur Devis' ? plan.price : `${plan.price} XOF`}
                    </Text>
                    {plan.period && (
                      <Text className="pricing-plan-period">
                        {plan.period}
                      </Text>
                    )}
                    {plan.save && (
                      <div className="pricing-plan-save">
                        <Badge 
                          count={plan.save} 
                          className="pricing-plan-save-badge"
                        />
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <Paragraph className="pricing-plan-description">
                    {plan.description}
                  </Paragraph>

                  {/* Bouton CTA avec nouvelles couleurs */}
                  <Link to={plan.name === 'Excellence' ? '/contact' : '/register'}>
                    <Button 
                      type={plan.popular ? 'primary' : 'default'}
                      block
                      size="large"
                      className={`pricing-plan-cta ${plan.popular ? 'popular' : ''}`}
                      style={{
                        backgroundColor: plan.popular ? plan.color : 'transparent',
                        borderColor: plan.color,
                        color: plan.popular ? 'white' : plan.color,
                      }}
                    >
                      {plan.buttonText}
                      <ChevronRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>

                  <Divider style={{ margin: '24px 0' }}>Inclus</Divider>
                  
                  {/* Liste des fonctionnalités */}
                  <div className="pricing-plan-features">
                    {plan.features.slice(0, 6).map((feature, i) => (
                      <div key={i} className="pricing-plan-feature">
                        <CheckOutlined style={{ 
                          color: plan.color, 
                          fontSize: '14px',
                          marginTop: '2px',
                          flexShrink: 0
                        }} />
                        <Text className="pricing-plan-feature-text">
                          {feature}
                        </Text>
                      </div>
                    ))}
                    
                    {plan.features.length > 6 && (
                      <Text className="pricing-plan-more-features">
                        + {plan.features.length - 6} autres fonctionnalités
                      </Text>
                    )}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Tableau comparatif détaillé */}
      <section className="pricing-comparison-section">
        <div className="pricing-comparison-container">
          <div className="pricing-comparison-header">
            <Title level={2} className="pricing-comparison-title">
              Comparaison détaillée des fonctionnalités
            </Title>
            <Paragraph className="pricing-comparison-description">
              Toutes les fonctionnalités listées sont opérationnelles et testées en production.
            </Paragraph>
          </div>

          {/* Version mobile et desktop */}
          <div className="d-block d-lg-none">
            <div className="pricing-comparison-mobile">
              <div className="pricing-comparison-mobile-inner">
                {Object.entries(groupedData).map(([category, items]) => (
                  <div key={category} className="pricing-comparison-category">
                    <Title level={4} className="pricing-comparison-category-title">
                      {category === 'Communications WhatsApp' && <WhatsAppOutlined />}
                      {category === 'Gestion boutiques' && <ShopOutlined />}
                      {category === 'Intégration Shopify' && <ShoppingCartOutlined />}
                      {category === 'Point de Vente' && <TeamOutlined />}
                      {category === 'Livraisons' && <TeamOutlined />}
                      {category === 'Analytics' && <BarChartOutlined />}
                      {category === 'Support' && <SafetyOutlined />}
                      {category === 'Avancé' && <CrownOutlined />}
                      {category}
                    </Title>
                    <Table 
                      columns={columns.map(col => ({
                        ...col,
                        width: col.key === 'feature' ? '200px' : '120px'
                      }))}
                      dataSource={items.map((item, index) => ({ ...item, key: `${category}-${index}` }))}
                      pagination={false}
                      size="small"
                      scroll={{ x: '600px' }}
                      style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Version desktop */}
          <div className="d-none d-lg-block">
            <Tabs defaultActiveKey="all" size="large" centered>
              <TabPane tab="Toutes les fonctionnalités" key="all">
                <div style={{ overflowX: 'auto' }}>
                  {Object.entries(groupedData).map(([category, items]) => (
                    <div key={category} style={{ marginBottom: '48px' }}>
                      <Title level={4} style={{
                        color: '#22C55E',
                        fontSize: '18px',
                        fontWeight: 700,
                        marginBottom: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {category === 'Communications WhatsApp' && <WhatsAppOutlined />}
                        {category === 'Gestion boutiques' && <ShopOutlined />}
                        {category === 'Intégration Shopify' && <ShoppingCartOutlined />}
                        {category === 'Point de Vente' && <TeamOutlined />}
                        {category === 'Livraisons' && <TeamOutlined />}
                        {category === 'Analytics' && <BarChartOutlined />}
                        {category === 'Support' && <SafetyOutlined />}
                        {category === 'Avancé' && <CrownOutlined />}
                        {category}
                      </Title>
                      <Table 
                        columns={columns}
                        dataSource={items.map((item, index) => ({ ...item, key: `${category}-${index}` }))}
                        pagination={false}
                        size="middle"
                        style={{
                          backgroundColor: '#FAFAFA',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                      />
                    </div>
                  ))}
                </div>
              </TabPane>
              
              {Object.entries(groupedData).map(([category, items]) => (
                <TabPane tab={category} key={category}>
                  <Table 
                    columns={columns}
                    dataSource={items.map((item, index) => ({ ...item, key: `${category}-${index}` }))}
                    pagination={false}
                    size="large"
                  />
                </TabPane>
              ))}
            </Tabs>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pricing-faq-section">
        <div className="pricing-faq-container">
          <Title level={2} className="pricing-faq-title">
            Questions fréquentes
          </Title>
          
          <Row gutter={[48, 32]}>
            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Les fonctionnalités sont-elles vraiment opérationnelles ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Oui, toutes les fonctionnalités listées dans nos plans sont développées, testées et opérationnelles. Mercuri Shop est déjà utilisé en production.
              </Paragraph>
            </Col>
            
            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Puis-je changer de plan à tout moment ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Oui, vous pouvez passer à un plan supérieur à tout moment. Le changement prend effet immédiatement et la facturation est ajustée au prorata.
              </Paragraph>
            </Col>
            
            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Le plan Découverte a-t-il des limitations ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Oui, le plan Découverte offre 15 messages automatisés puis déconnexion automatique. Durée maximale : 2 semaines même si les messages ne sont pas utilisés. Ce plan n'est pas renouvelable.
              </Paragraph>
            </Col>
            
            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Que se passe-t-il après les 15 messages du plan Découverte ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Votre WhatsApp est automatiquement déconnecté de Mercuri Shop. Pour continuer, vous devez passer à un plan payant (Essentiel, Croissance ou Excellence).
              </Paragraph>
            </Col>

            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Y a-t-il une période d'essai gratuite ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Le plan Découverte sert de période d'essai avec 15 messages pour tester Mercuri Shop. Pour un usage continu, choisissez un plan payant adapté à vos besoins.
              </Paragraph>
            </Col>

            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Quels modes de paiement acceptez-vous ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Nous acceptons les paiements par carte bancaire, Mobile Money (Orange Money, Wave, etc.) et virement bancaire pour les plans Excellence.
              </Paragraph>
            </Col>

            <Col xs={24} md={12}>
              <Title level={4} className="pricing-faq-question">
                Puis-je avoir plus de 2 numéros WhatsApp ?
              </Title>
              <Paragraph className="pricing-faq-answer">
                Le plan Excellence inclut 2 numéros WhatsApp. Pour des besoins spécifiques nécessitant plus de numéros, contactez notre équipe commerciale pour une solution sur mesure.
              </Paragraph>
            </Col>
          </Row>
        </div>
      </section>

      {/* CTA Section avec nouvelles couleurs */}
      <section className="pricing-cta-section">
        <div className="pricing-cta-container">
          <Title level={2} className="pricing-cta-title">
            Prêt à automatiser votre business WhatsApp ?
          </Title>
          <Paragraph className="pricing-cta-description">
            Rejoignez les entreprises qui font confiance à Mercuri Shop pour développer leur activité avec des fonctionnalités éprouvées.
          </Paragraph>
          
          <Space size="large" className="pricing-cta-buttons">
            <Link to="/register">
              <Button 
                size="large"
                className="pricing-cta-primary"
              >
                Commencer gratuitement
                <ChevronRightIcon className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="large"
                ghost
                className="pricing-cta-secondary"
              >
                Demander une démo
              </Button>
            </Link>
          </Space>
        </div>
      </section>
    </div>
  );
};

export default Pricing; 